/**
 * Field format validators and input masks for Brazilian legal documents.
 */

// ============ Mask Functions ============

export function maskCPF(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

export function maskCNPJ(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

export function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d{1,4})$/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d{1,4})$/, '$1-$2');
}

export function maskCEP(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.replace(/(\d{5})(\d{1,3})$/, '$1-$2');
}

export function maskProcessNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 20);
  return digits
    .replace(/(\d{7})(\d)/, '$1-$2')
    .replace(/(-\d{2})(\d)/, '$1.$2')
    .replace(/(\.\d{4})(\d)/, '$1.$2')
    .replace(/(\.\d)(\d)/, '$1.$2')
    .replace(/(\.\d{2})(\d{1,4})$/, '$1.$2');
}

export function maskDateBR(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\d{2})(\d{1,4})$/, '$1/$2');
}

export function applyMask(value: string, format: string): string {
  const masks: Record<string, (v: string) => string> = {
    cpf: maskCPF,
    cnpj: maskCNPJ,
    phone: maskPhone,
    cep: maskCEP,
    process_number: maskProcessNumber,
    date_br: maskDateBR,
  };
  const fn = masks[format];
  return fn ? fn(value) : value;
}

// ============ Validation Functions ============

function cpfChecksum(digits: number[]): boolean {
  if (digits.length !== 11 || digits.every(d => d === digits[0])) return false;
  for (let i = 9; i < 11; i++) {
    let sum = 0;
    for (let j = 0; j < i; j++) {
      sum += digits[j] * (i + 1 - j);
    }
    const digit = ((sum * 10) % 11) % 10;
    if (digits[i] !== digit) return false;
  }
  return true;
}

function cnpjChecksum(digits: number[]): boolean {
  if (digits.length !== 14 || digits.every(d => d === digits[0])) return false;
  const w1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const w2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  for (const [weights, idx] of [[w1, 12], [w2, 13]] as [number[], number][]) {
    let sum = 0;
    for (let i = 0; i < weights.length; i++) sum += digits[i] * weights[i];
    let digit = 11 - (sum % 11);
    if (digit >= 10) digit = 0;
    if (digits[idx] !== digit) return false;
  }
  return true;
}

export function validateCPF(value: string): { valid: boolean; error?: string } {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 11) return { valid: false, error: 'CPF deve ter 11 digitos' };
  if (!cpfChecksum(digits.split('').map(Number))) return { valid: false, error: 'CPF invalido' };
  return { valid: true };
}

export function validateCNPJ(value: string): { valid: boolean; error?: string } {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 14) return { valid: false, error: 'CNPJ deve ter 14 digitos' };
  if (!cnpjChecksum(digits.split('').map(Number))) return { valid: false, error: 'CNPJ invalido' };
  return { valid: true };
}

export function validateEmail(value: string): { valid: boolean; error?: string } {
  const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  if (!pattern.test(value)) return { valid: false, error: 'Email invalido' };
  return { valid: true };
}

export function validatePhone(value: string): { valid: boolean; error?: string } {
  const digits = value.replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 11) return { valid: false, error: 'Telefone invalido' };
  return { valid: true };
}

export function validateCEP(value: string): { valid: boolean; error?: string } {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 8) return { valid: false, error: 'CEP deve ter 8 digitos' };
  return { valid: true };
}

export function validateOAB(value: string): { valid: boolean; error?: string } {
  const cleaned = value.replace(/[^a-zA-Z0-9]/g, '');
  if (cleaned.length < 4) return { valid: false, error: 'Numero OAB invalido' };
  return { valid: true };
}

export function validateProcessNumber(value: string): { valid: boolean; error?: string } {
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 20) return { valid: false, error: 'Numero do processo deve ter 20 digitos (formato CNJ)' };
  return { valid: true };
}

export function validateDateBR(value: string): { valid: boolean; error?: string } {
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return { valid: false, error: 'Data deve estar no formato dd/mm/aaaa' };
  const [, d, m, y] = match.map(Number);
  if (m < 1 || m > 12) return { valid: false, error: 'Mes invalido' };
  if (d < 1 || d > 31) return { valid: false, error: 'Dia invalido' };
  if (y < 1900 || y > 2100) return { valid: false, error: 'Ano invalido' };
  return { valid: true };
}

const VALIDATORS: Record<string, (v: string) => { valid: boolean; error?: string }> = {
  cpf: validateCPF,
  cnpj: validateCNPJ,
  email: validateEmail,
  phone: validatePhone,
  cep: validateCEP,
  oab: validateOAB,
  process_number: validateProcessNumber,
  date_br: validateDateBR,
};

export function validateFormat(value: string, format: string): { valid: boolean; error?: string } {
  if (!value || !format) return { valid: true };
  const fn = VALIDATORS[format];
  return fn ? fn(value) : { valid: true };
}

export function getFormatPlaceholder(format: string): string {
  const placeholders: Record<string, string> = {
    cpf: '000.000.000-00',
    cnpj: '00.000.000/0000-00',
    email: 'email@exemplo.com',
    phone: '(00) 00000-0000',
    cep: '00000-000',
    oab: 'Ex: 123456/SP',
    process_number: '0000000-00.0000.0.00.0000',
    date_br: 'dd/mm/aaaa',
  };
  return placeholders[format] || '';
}

export function getFormatLabel(format: string): string {
  const labels: Record<string, string> = {
    cpf: 'CPF',
    cnpj: 'CNPJ',
    email: 'Email',
    phone: 'Telefone',
    cep: 'CEP',
    oab: 'OAB',
    process_number: 'N. Processo (CNJ)',
    date_br: 'Data (dd/mm/aaaa)',
  };
  return labels[format] || format;
}

export const AVAILABLE_FORMATS = [
  { value: 'cpf', label: 'CPF' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Telefone' },
  { value: 'cep', label: 'CEP' },
  { value: 'oab', label: 'OAB' },
  { value: 'process_number', label: 'N. Processo (CNJ)' },
  { value: 'date_br', label: 'Data (dd/mm/aaaa)' },
];
